/**
 * Comet Tracking Integration for Shopify
 * Tracks Add to Cart and Checkout events
 */

class CometTracking {
  constructor() {
    this.checkoutFired = false;
    this.init();
  }

  init() {
    // Debug: Check if Comet exists
    console.log('🔍 Comet Tracking initializing...');
    console.log('   Comet function:', typeof window.comet);
    
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.setupTracking();
      });
    } else {
      this.setupTracking();
    }
  }

  setupTracking() {
    console.log('✅ Setting up Comet tracking listeners...');
    this.trackAddToCart();
    this.trackAddToCartViaFetch(); // Backup method via fetch interception
    this.trackCheckout();
    this.trackAcceleratedCheckout();
    console.log('✅ Comet tracking ready!');
  }

  /**
   * Track Add to Cart - Only fires on actual cart additions
   * NOT when cart drawer opens, cart is viewed, or quick add modal opens
   */
  trackAddToCart() {
    console.log('🔧 Installing add to cart listener...');
    
    // Listen to ALL possible cart events for debugging
    const cartEvents = ['theme:cart:add', 'cart:add', 'product:added', 'cart:updated'];
    
    cartEvents.forEach(eventName => {
      document.addEventListener(eventName, (event) => {
        console.log(`📦 Received event: ${eventName}`, event.detail);
      });
    });
    
    // Main listener for theme:cart:add
    document.addEventListener('theme:cart:add', (event) => {
      console.log('🎯 theme:cart:add event fired!', event.detail);
      
      // Only fire if it's a successful add (not an error)
      if (event.detail?.data?.didError) {
        console.log('Comet: Skipping add_to_cart (error occurred)');
        return;
      }

      // Verify this is from an actual form submission (product-form-component source)
      const source = event.detail?.data?.source;
      if (source && source !== 'product-form-component') {
        console.log('Comet: Skipping add_to_cart (not from product form, source:', source, ')');
        return;
      }

      console.log('Comet: Tracking add_to_cart', {
        source: source,
        productId: event.detail?.data?.productId,
        itemCount: event.detail?.data?.itemCount
      });
      
      // Fire Comet tracking
      if (typeof window.comet === 'function') {
        console.log('🔥 Firing comet("add_to_cart")');
        window.comet('add_to_cart');
        console.log('✅ comet("add_to_cart") fired successfully');
      } else {
        console.error('❌ Comet function not found. Make sure Comet script is loaded.');
        console.log('   window.comet type:', typeof window.comet);
      }
    });
    
    console.log('✅ Add to cart listener installed');
  }

  /**
   * Track Add to Cart via Fetch API (Backup Method)
   * This intercepts the cart/add.js API call directly
   * Works even if theme events don't fire
   */
  trackAddToCartViaFetch() {
    console.log('🔧 Installing fetch-based add to cart tracker...');
    
    // Store original fetch
    const originalFetch = window.fetch;
    const self = this;
    
    // Intercept fetch
    window.fetch = function(...args) {
      const url = args[0];
      
      // Check if this is a cart add request
      if (typeof url === 'string' && url.includes('/cart/add')) {
        console.log('🛒 Cart add request detected');
        
        return originalFetch.apply(this, args).then(response => {
          // Clone response to read it without consuming it
          const clonedResponse = response.clone();
          
          // Check if successful
          if (response.ok) {
            clonedResponse.json().then(data => {
              // Check if it's a real success (not an error response with 200 status)
              if (!data.status || data.status === 200) {
                console.log('Comet: Tracking add_to_cart (via fetch)', {
                  productId: data.product_id,
                  variantId: data.variant_id,
                  quantity: data.quantity
                });
                
                // Fire Comet tracking
                if (typeof window.comet === 'function') {
                  console.log('🔥 Firing comet("add_to_cart")');
                  window.comet('add_to_cart');
                  console.log('✅ comet("add_to_cart") fired successfully');
                }
              } else {
                console.log('Comet: Skipping add_to_cart (error in response)', data);
              }
            }).catch(() => {
              // If can't parse JSON, assume success
              console.log('Comet: Tracking add_to_cart (via fetch, non-JSON response)');
              if (typeof window.comet === 'function') {
                window.comet('add_to_cart');
              }
            });
          }
          
          return response;
        });
      }
      
      return originalFetch.apply(this, args);
    };
    
    console.log('✅ Fetch-based tracker installed');
  }

  /**
   * Track Regular Checkout Button
   * Only fires when the actual checkout button is clicked and form is submitting
   */
  trackCheckout() {
    // Find the cart form
    const cartForm = document.querySelector('form[action="/cart"]');
    
    if (cartForm) {
      // Listen to form submit event - this is the actual checkout action
      cartForm.addEventListener('submit', (event) => {
        // Check if the checkout button was clicked (not other form submissions)
        const checkoutButton = event.submitter;
        
        if (checkoutButton && checkoutButton.getAttribute('name') === 'checkout') {
          // Prevent duplicate firing
          if (this.checkoutFired) {
            console.log('Comet: Skipping duplicate checkout event');
            return;
          }
          
          this.checkoutFired = true;
          
          console.log('Comet: Tracking initiate_checkout (standard)');
          
          // Fire Comet tracking
          if (typeof window.comet === 'function') {
            window.comet('initiate_checkout');
          } else {
            console.warn('Comet function not found. Make sure Comet script is loaded.');
          }
        }
      });
    }
  }

  /**
   * Track Shop Pay / Accelerated Checkout Buttons
   * Shop Pay uses web components with shadow DOM, so we track navigation to /checkout instead
   */
  trackAcceleratedCheckout() {
    console.log('🔧 Installing accelerated checkout tracker...');
    
    // Method 1: Intercept navigation to checkout
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    const self = this;
    
    // Override pushState
    history.pushState = function(...args) {
      const url = args[2];
      if (url && url.includes('/checkout')) {
        self.fireAcceleratedCheckout('pushState');
      }
      return originalPushState.apply(this, args);
    };
    
    // Override replaceState
    history.replaceState = function(...args) {
      const url = args[2];
      if (url && url.includes('/checkout')) {
        self.fireAcceleratedCheckout('replaceState');
      }
      return originalReplaceState.apply(this, args);
    };
    
    // Method 2: Monitor window location changes
    let lastUrl = location.href;
    new MutationObserver(() => {
      const currentUrl = location.href;
      if (currentUrl !== lastUrl) {
        lastUrl = currentUrl;
        if (currentUrl.includes('/checkout')) {
          self.fireAcceleratedCheckout('navigation');
        }
      }
    }).observe(document, { subtree: true, childList: true });
    
    // Method 3: Intercept form submissions (Shop Pay often submits to checkout)
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (form instanceof HTMLFormElement) {
        const action = form.action || form.getAttribute('action');
        if (action && action.includes('/checkout')) {
          self.fireAcceleratedCheckout('form-submit');
        }
      }
    }, true); // Use capture phase
    
    // Method 4: Monitor clicks on accelerated checkout containers
    const containers = document.querySelectorAll('.additional-checkout-buttons, [ref="acceleratedCheckoutButtonContainer"], [data-shopify="payment-button"]');
    containers.forEach(container => {
      container.addEventListener('click', (event) => {
        console.log('🖱️ Click detected in accelerated checkout container');
        console.log('   Target:', event.target);
        console.log('   Current URL:', window.location.href);
        
        // Fire tracking immediately on click
        // Shop Pay buttons are inside shadow DOM, so any click in the container is likely checkout
        if (!self.checkoutFired) {
          self.fireAcceleratedCheckout('container-click-immediate');
        }
      }, true);
    });
    
    console.log('✅ Accelerated checkout tracker installed');
  }
  
  /**
   * Fire accelerated checkout tracking
   */
  fireAcceleratedCheckout(method) {
    if (this.checkoutFired) {
      console.log('Comet: Skipping duplicate accelerated checkout');
      return;
    }
    
    this.checkoutFired = true;
    
    console.log(`Comet: Tracking initiate_checkout (accelerated via ${method})`);
    
    if (typeof window.comet === 'function') {
      console.log('🔥 Firing comet("initiate_checkout")');
      window.comet('initiate_checkout');
      console.log('✅ comet("initiate_checkout") fired successfully');
    } else {
      console.warn('Comet function not found. Make sure Comet script is loaded.');
    }
  }
}

// Initialize tracking
new CometTracking();

